import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTelegram } from '../contexts/TelegramContext';
import { userAPI, inviteAPI } from '../services/api';
import {
  KeyIcon,
  BellIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  ExclamationTriangleIcon,
  CreditCardIcon,
  BanknotesIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import { formatDate, formatCurrency } from '../utils/format';

export const Profile: React.FC = () => {
  const { user, updateUser } = useAuth();
  const { isInTelegram, openLink, openTelegramLink } = useTelegram();
  const [passwordLoading, setPasswordLoading] = useState(false);
  // const [notificationLoading, setNotificationLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [transferLoading, setTransferLoading] = useState(false);
  const [withdrawLoading, setWithdrawLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [botInfo, setBotInfo] = useState<any>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [transferAmount, setTransferAmount] = useState('');
  const [withdrawAccount, setWithdrawAccount] = useState('');
  const [withdrawMethod, setWithdrawMethod] = useState('');
  const [config, setConfig] = useState<any>(null);
  const [userConfig, setUserConfig] = useState<any>(null);
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
    remind_expire: user?.remind_expire ? true : false,
    remind_traffic: user?.remind_traffic ? true : false,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [botResponse, configResponse, userConfigResponse] = await Promise.all([
          userAPI.getBotInfo(),
          userAPI.getConfig(),
          userAPI.getConfig() // 获取用户配置
        ]);
        setBotInfo(botResponse.data.data);

        setConfig(configResponse.data.data);
        const userConfigData = userConfigResponse.data.data;
        setUserConfig(userConfigData);

        // 设置默认提现方式
        if (userConfigData?.withdraw_methods && userConfigData.withdraw_methods.length > 0) {
          setWithdrawMethod(userConfigData.withdraw_methods[0]);
        }
      } catch (error) {
        console.error('Failed to fetch data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        remind_expire: user.remind_expire ? true : false,
        remind_traffic: user.remind_traffic ? true : false,
      }));
    }
  }, [user]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleNotificationUpdate = async (field: string, value: boolean) => {
    // setNotificationLoading(true);
    setMessage('');

    // 更新本地状态
    const newFormData = {
      ...formData,
      [field]: value
    };
    setFormData(newFormData);

    try {
      await userAPI.updateRemindSettings({
        remind_expire: field === 'remind_expire' ? (value ? 1 : 0) : (formData.remind_expire ? 1 : 0),
        remind_traffic: field === 'remind_traffic' ? (value ? 1 : 0) : (formData.remind_traffic ? 1 : 0),
      });
      updateUser({
        remind_expire: field === 'remind_expire' ? (value ? 1 : 0) : (formData.remind_expire ? 1 : 0),
        remind_traffic: field === 'remind_traffic' ? (value ? 1 : 0) : (formData.remind_traffic ? 1 : 0)
      });
      setMessage('提醒设置更新成功！');
    } catch (error: any) {
      // 如果API调用失败，回滚状态
      setFormData(formData);
      setMessage(error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat()[0]
        : error.response?.data?.message || '更新失败，请稍后重试');
    } finally {
      // setNotificationLoading(false);
    }
  };

  const handleResetSecurity = async () => {
    if (!window.confirm('确定要重置订阅UUID吗？重置后需要重新配置客户端。')) {
      return;
    }

    setResetLoading(true);
    setMessage('');

    try {
      await userAPI.resetSecurity();
      setMessage('订阅UUID重置成功！请重新获取订阅链接。');
      // 刷新用户信息
      window.location.reload();
    } catch (error: any) {
      setMessage(error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat()[0]
        : error.response?.data?.message || '重置失败，请稍后重试');
    } finally {
      setResetLoading(false);
    }
  };

  const handleTransferToBalance = async () => {
    if (!transferAmount || parseFloat(transferAmount) <= 0) {
      setMessage('请输入有效的划转金额');
      return;
    }

    if (user && parseFloat(transferAmount) > user.commission_balance / 100) {
      setMessage('划转金额不能超过佣金余额');
      return;
    }

    setTransferLoading(true);
    setMessage('');

    try {
      await inviteAPI.transferToBalance((parseFloat(transferAmount) * 100).toString());
      setMessage('划转成功！');
      setShowTransferModal(false);
      setTransferAmount('');
      // 刷新用户信息
      window.location.reload();
    } catch (error: any) {
      setMessage(error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat()[0]
        : error.response?.data?.message || '划转失败，请稍后重试');
    } finally {
      setTransferLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAccount.trim()) {
      setMessage('请输入提现账户');
      return;
    }

    setWithdrawLoading(true);
    setMessage('');

    try {
      await inviteAPI.withdraw(withdrawAccount, withdrawMethod);
      setMessage('提现申请已提交，请等待审核！');
      setShowWithdrawModal(false);
      setWithdrawAccount('');
    } catch (error: any) {
      setMessage(error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat()[0]
        : error.response?.data?.message || '提现申请失败，请稍后重试');
    } finally {
      setWithdrawLoading(false);
    }
  };

  const getTelegramBindUrl = () => {
    // if (!botInfo?.username || !user?.token) {
    //   return '#';
    // }
    return `https://t.me/${botInfo.username}?start`;
  };

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setPasswordLoading(true);
    setMessage('');

    if (formData.newPassword !== formData.confirmPassword) {
      setMessage('两次输入的新密码不一致');
      setPasswordLoading(false);
      return;
    }

    if (formData.newPassword.length < 6) {
      setMessage('新密码长度至少为6位');
      setPasswordLoading(false);
      return;
    }

    try {
      await userAPI.changePassword(
        formData.currentPassword,
        formData.newPassword,
      );
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
      setMessage('密码修改成功！');
    } catch (error: any) {
      setMessage(error.response?.data?.errors
        ? Object.values(error.response.data.errors).flat()[0]
        : error.response?.data?.message || '密码修改失败，请检查当前密码');
    } finally {
      setPasswordLoading(false);
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white transition-colors">个人资料</h1>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-400 transition-colors">
          管理您的账户信息和安全设置。
        </p>
      </div>

      {/* 余额信息 */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="flex items-center mb-4">
          <CreditCardIcon className="h-5 w-5 text-gray-400 mr-2" />
          <h2 className="text-lg font-medium text-gray-900">账户余额</h2>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-600">账户余额</p>
                <p className="text-2xl font-bold text-blue-900">
                  {user ? formatCurrency(user.balance) : '¥0.00'}
                </p>
              </div>
              <div className="p-3 bg-blue-200 rounded-full">
                <CreditCardIcon className="h-6 w-6 text-blue-600" />
              </div>
            </div>
            <p className="mt-2 text-xs text-blue-600">
              可用于购买套餐和续费
            </p>
          </div>
          <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-green-600">佣金余额</p>
                <p className="text-2xl font-bold text-green-900">
                  {user ? formatCurrency(user.commission_balance) : '¥0.00'}
                </p>
              </div>
              <div className="p-3 bg-green-200 rounded-full">
                <CreditCardIcon className="h-6 w-6 text-green-600" />
              </div>
            </div>
            <p className="mt-2 text-xs text-green-600">
              推广获得的佣金收入
            </p>
            <div className="mt-4 flex flex-col sm:flex-row gap-2">
              <button
                onClick={() => setShowTransferModal(true)}
                disabled={!user?.commission_balance || user.commission_balance <= 0}
                className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-green-600 text-white text-sm rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowRightIcon className="h-4 w-4 mr-1" />
                划转到余额
              </button>
              {userConfig && !userConfig.withdraw_close && (
                <button
                  onClick={() => setShowWithdrawModal(true)}
                  disabled={!user?.commission_balance || user.commission_balance <= 0}
                  className="flex-1 inline-flex items-center justify-center px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <BanknotesIcon className="h-4 w-4 mr-1" />
                  提现
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {message && (
        <div className={`mb-6 p-4 rounded-md ${message.includes('成功')
          ? 'bg-green-50 border border-green-200 text-green-700'
          : 'bg-red-50 border border-red-200 text-red-700'
          }`}>
          {message}
        </div>
      )}

      <div className="space-y-6">


        {/* 修改密码 */}
        {!isInTelegram && (
          <div className="bg-white shadow rounded-lg p-6">
            <div className="flex items-center mb-4">
              <KeyIcon className="h-5 w-5 text-gray-400 mr-2" />
              <h2 className="text-lg font-medium text-gray-900">修改密码</h2>
            </div>
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700">
                  当前密码
                </label>
                <input
                  type="password"
                  name="currentPassword"
                  id="currentPassword"
                  value={formData.currentPassword}
                  onChange={handleInputChange}
                  className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  placeholder="请输入当前密码"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    新密码
                  </label>
                  <input
                    type="password"
                    name="newPassword"
                    id="newPassword"
                    value={formData.newPassword}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请输入新密码（至少8位）"
                  />
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    确认新密码
                  </label>
                  <input
                    type="password"
                    name="confirmPassword"
                    id="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请再次输入新密码"
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={passwordLoading}
                  className="w-full sm:w-auto bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {passwordLoading ? '修改中...' : '修改密码'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* 提醒设置 */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <BellIcon className="h-5 w-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">提醒设置</h2>
          </div>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="remind_expire" className="text-sm font-medium text-gray-700">
                  到期提醒
                </label>
                <p className="text-sm text-gray-500">在套餐到期前通过邮件提醒您</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="remind_expire"
                  id="remind_expire"
                  checked={formData.remind_expire}
                  onChange={(e) => {
                    handleNotificationUpdate('remind_expire', e.target.checked);
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>

            <div className="flex items-center justify-between">
              <div>
                <label htmlFor="remind_traffic" className="text-sm font-medium text-gray-700">
                  流量提醒
                </label>
                <p className="text-sm text-gray-500">在流量使用达到一定比例时通过邮件提醒您</p>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  name="remind_traffic"
                  id="remind_traffic"
                  checked={formData.remind_traffic}
                  onChange={(e) => {
                    handleNotificationUpdate('remind_traffic', e.target.checked);
                  }}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
              </label>
            </div>
          </div>
        </div>

        {/* Telegram 功能 */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <ChatBubbleLeftRightIcon className="h-5 w-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">Telegram 功能</h2>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <p className="text-sm text-blue-700">
              绑定 Telegram 后可以接收重要通知，并加入官方群组获取最新资讯。
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {!isInTelegram && (
              <div className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-medium text-gray-900">绑定 Telegram Bot</h4>
                  {user?.telegram_id && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                      已绑定
                    </span>
                  )}
                </div>
                {botInfo ? (
                  <div className="space-y-3">
                    {user?.telegram_id ? (
                      <div className="bg-green-50 border border-green-200 rounded-md p-3">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-green-800">
                              Telegram Bot 已成功绑定
                            </p>
                            <p className="text-sm text-green-700 mt-1">
                              您将通过 Telegram 接收重要通知
                            </p>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <>
                        <p className="text-sm text-gray-600">
                          绑定 Telegram Bot 后可以接收账户余额、流量使用、订阅到期等重要通知。
                        </p>
                        {/* <div className="bg-gray-50 rounded-md p-3">
                          <p className="text-xs text-gray-500 mb-2">绑定步骤：</p>
                          <ol className="text-xs text-gray-600 space-y-1">
                            <li>1. 点击下方按钮打开 Telegram Bot</li>
                            <li>2. 发送 /start 命令开始绑定</li>
                            <li>3. 按照提示完成身份验证</li>
                          </ol>
                        </div> */}
                        <a
                          href={getTelegramBindUrl()}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors text-sm"
                        >
                          <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2"/>
                          绑定官方 Bot
                        </a>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-6">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-3 text-sm text-gray-500">正在加载 Bot 信息...</p>
                  </div>
                )}
              </div>
            )}

            <div className="border border-gray-200 rounded-lg p-4">
              <h4 className="font-medium text-gray-900 mb-3">加入官方群组</h4>
              {config?.telegram_discuss_link ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    加入我们的官方 Telegram 群组，获取最新资讯和技术支持。
                  </p>
                  {isInTelegram ? (
                    <button
                      onClick={() => openLink(config.telegram_discuss_link)}
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors text-sm"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                      加入官方群组
                    </button>
                  ) : (
                    <a
                      href={config.telegram_discuss_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors text-sm"
                    >
                      <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                      加入官方群组
                    </a>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-500">暂无群组链接</p>
              )}
            </div>
          </div>
        </div>

        {/* 订阅管理 */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center mb-4">
            <ArrowPathIcon className="h-5 w-5 text-gray-400 mr-2" />
            <h2 className="text-lg font-medium text-gray-900">订阅管理</h2>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400 mt-0.5 mr-3 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-yellow-800">重要提示</h3>
                <p className="mt-1 text-sm text-yellow-700">
                  重置订阅 UUID 后，您需要重新配置所有客户端。请确保您已保存当前配置信息。
                </p>
              </div>
            </div>
          </div>

          <div className="border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-3">重置订阅 UUID</h4>
            <div className="space-y-3">
              <p className="text-sm text-gray-600">
                如果您的订阅链接泄露或需要更换设备，可以重置订阅 UUID。重置后原有的订阅链接将失效。
              </p>
              <div className="bg-gray-50 p-3 rounded-md">
                <p className="text-sm text-gray-700 font-medium">重置后的影响：</p>
                <ul className="mt-1 text-sm text-gray-600 list-disc list-inside space-y-1">
                  <li>所有客户端需要重新配置</li>
                  <li>原有订阅链接立即失效</li>
                  <li>需要重新获取新的订阅链接</li>
                </ul>
              </div>
              <button
                onClick={handleResetSecurity}
                disabled={resetLoading}
                className="w-full sm:w-auto inline-flex items-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <ArrowPathIcon className="h-4 w-4 mr-2" />
                {resetLoading ? '重置中...' : '重置订阅 UUID'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 划转到余额模态框 */}
      {showTransferModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">划转到余额</h3>
                <button
                  onClick={() => {
                    setShowTransferModal(false);
                    setTransferAmount('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">关闭</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-2">
                    当前佣金余额: <span className="font-medium text-green-600">{user ? formatCurrency(user.commission_balance) : '¥0.00'}</span>
                  </p>
                  <label htmlFor="transferAmount" className="block text-sm font-medium text-gray-700">
                    划转金额 (元)
                  </label>
                  <input
                    type="number"
                    id="transferAmount"
                    value={transferAmount}
                    onChange={(e) => setTransferAmount(e.target.value)}
                    min="0"
                    max={user ? (user.commission_balance / 100).toString() : '0'}
                    step="0.01"
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder="请输入划转金额"
                  />
                </div>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                  <p className="text-sm text-yellow-700">
                    <strong>提示:</strong> 划转后的金额将添加到您的账户余额中，可用于购买套餐和续费。
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowTransferModal(false);
                      setTransferAmount('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleTransferToBalance}
                    disabled={transferLoading || !transferAmount || parseFloat(transferAmount) <= 0}
                    className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {transferLoading ? '划转中...' : '确认划转'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 提现模态框 */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">申请提现</h3>
                <button
                  onClick={() => {
                    setShowWithdrawModal(false);
                    setWithdrawAccount('');
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">关闭</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-gray-600 mb-4">
                    当前佣金余额: <span className="font-medium text-green-600">{user ? formatCurrency(user.commission_balance) : '¥0.00'}</span>
                  </p>
                  <label htmlFor="withdrawMethod" className="block text-sm font-medium text-gray-700">
                    提现方式
                  </label>
                  <select
                    id="withdrawMethod"
                    value={withdrawMethod}
                    onChange={(e) => setWithdrawMethod(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                  >
                    {userConfig?.withdraw_methods?.map((method: string) => (
                      <option key={method} value={method}>
                        {method}
                      </option>
                    )) || (
                        <>
                          <option value="支付宝">支付宝</option>
                          <option value="微信">微信</option>
                          <option value="银行卡">银行卡</option>
                        </>
                      )}
                  </select>
                </div>
                <div>
                  <label htmlFor="withdrawAccount" className="block text-sm font-medium text-gray-700">
                    {withdrawMethod}账号
                  </label>
                  <input
                    type="text"
                    id="withdrawAccount"
                    value={withdrawAccount}
                    onChange={(e) => setWithdrawAccount(e.target.value)}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
                    placeholder={`请输入${withdrawMethod}账号`}
                  />
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm text-blue-700">
                    <strong>说明:</strong> 提现申请提交后将进入审核流程，审核通过后会将全部佣金余额提现到您指定的账户。
                  </p>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowWithdrawModal(false);
                      setWithdrawAccount('');
                    }}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleWithdraw}
                    disabled={withdrawLoading || !withdrawAccount.trim()}
                    className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {withdrawLoading ? '提交中...' : '提交申请'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};